import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'

// Actions
import { actions as subscriptionActions } from 'modules/subscription/actions'

// Components
import { Section, Spinner, Link } from '@tracklib/kit'
import { OfferPill, OfferTitle, OfferPerks, OfferButton } from 'components/subscribe/offer'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useSignupCode } from 'modules/signup/hooks'
import { useProfile } from 'modules/profile/hooks'
import { useIsAuthenticated } from 'modules/auth/hooks'

// Types
import { TierOffer, State, TierPeriod } from 'types'

// Utils
import { priceString } from 'components/base/price'
import { CURRENCY } from '@constants'

import styles from './offersblock.css'

type OfferProps = {
  key: string
  description: string
  label: string
  downloads: number
  periods: Array<TierPeriod>
  currency: string
}

const Offer = ({ key, description, label, downloads, periods, currency }: OfferProps): JSX.Element => {
  const hasDiscount = periods.find(({ months }) => months === 1)?.price !== periods.find(({ months }) => months === 1)?.discount_offer_price
  const _description = hasDiscount ? 'LIMITED OFFER' : description
  const priceYear = periods.find(({ months }) => months === 12)?.price as number
  const priceMonth = periods.find(({ months }) => months === 1)?.price as number
  return (
    <Link
      key={key}
      to="/subscribe/"
      className={clsx(styles.offer, hasDiscount && styles.discountOffer, label === 'Standard' && !hasDiscount && styles.offerPreferred)}
    >
      {description && (
        <OfferPill offer={label} hasDiscount={hasDiscount}>
          {_description}
        </OfferPill>
      )}
      {label && <OfferTitle>{label}</OfferTitle>}
      {hasDiscount ? (
        <div className={styles.periodsWrapper}>
          {periods.map(period => {
            const discountedPrice = priceString(period?.discount_offer_price, currency)
            return (
              <div className={styles.periods}>
                <div className={clsx(styles.period)}>
                  <span className={styles.priceNew}>{`${discountedPrice}${period.months === 1 ? '/mo' : '/yr'}`}</span>
                </div>
                <div className={clsx(styles.period)}>
                  <span className={styles.priceOld}>{`${priceString(period.price, currency)}${period.months === 1 ? '/mo' : '/yr'}`}</span>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <>
          <p className={styles.priceMonth}>{`${priceString(priceMonth, currency)}/mo`}</p>
          <p className={styles.priceYear}>{`${priceString(priceYear, currency)}/year when paying annually`}</p>
        </>
      )}
      <OfferPerks>{downloads}</OfferPerks>
      <OfferButton offer={label} hasDiscount={hasDiscount} className={styles.offerButton}>
        Get started
      </OfferButton>
    </Link>
  )
}

const OffersBlock = () => {
  const dispatch = useDispatch()
  const { refcode } = useSignupCode()
  const fetchTierOffersRequest = useRequest('FETCH_TIER_OFFERS', refcode)
  const offers = useSelector<State, Array<TierOffer>>(state => Object.values(state.subscription[refcode] || {}))
  const isAuth = useIsAuthenticated()
  const profile = useProfile()
  const currency = isAuth ? profile.currency : CURRENCY
  useEffect(() => {
    if (!fetchTierOffersRequest.isLoading && !fetchTierOffersRequest.isDone) {
      dispatch(subscriptionActions.fetchTierOffers(refcode))
    }
  }, [dispatch, refcode, fetchTierOffersRequest])
  if (!offers) {
    return <Spinner minHeight={400} delay={0} />
  }

  return (
    <Section extraPad className={styles.sectionWrapper}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {offers
            .sort((a, b) => a.tier.ordering - b.tier.ordering)
            .map(({ tier: { id, description, label, credits_give_per_month, periods } }) => {
              return (
                <Offer
                  key={String(id)}
                  description={description}
                  label={label}
                  downloads={credits_give_per_month}
                  periods={periods}
                  currency={currency}
                />
              )
            })}
        </div>
      </div>
    </Section>
  )
}

export default OffersBlock
